import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Clock, MessageCircle, Unplug, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Field, Input, useToast } from '@/components/ui';
import { ApiError, request } from '@/lib/api';
import { launchWhatsAppSignup, SignupCancelled } from '@/lib/whatsapp-signup';

/* ---------------------------------------------------------------------------
   WhatsApp: שליחה אוטומטית ללקוחות במקום התור הידני.

   חיבור בלחיצה (Embedded Signup): חלון של Meta — מתחברים עם פייסבוק,
   בוחרים או יוצרים מספר עסקי, וזהו. השרת מקבל טוקן, רושם את המספר ויוצר
   תבנית הודעה שנשלחת לאישור Meta. חיבור ידני (מזהה + טוקן) — למתקדמים,
   ולעסק שכבר יש לו אפליקציה משלו ב-Meta.
   --------------------------------------------------------------------------- */

const settingsSchema = z.object({
  phoneNumberId: z.string(),
  displayPhoneNumber: z.string(),
  verifiedName: z.string().nullable(),
  templateName: z.string().nullable(),
  templateLanguage: z.string(),
  templateParams: z.number().optional(),
  templateStatus: z.string().nullish(),
  connectedVia: z.enum(['embedded', 'manual']).optional(),
});
const statusSchema = z.object({
  connected: z.boolean(),
  status: z.string().nullable(),
  settings: settingsSchema.nullable(),
  lastError: z.string().nullable(),
  embedded: z.object({ appId: z.string(), configId: z.string() }).nullable(),
  demo: z.boolean().optional(),
});

const KEY = ['integrations', 'whatsapp'];
const TEMPLATE = /^[a-z0-9_]*$/;

export function WhatsAppCard() {
  const { t } = useTranslation('settings');
  const toast = useToast();
  const qc = useQueryClient();
  const status = useQuery({ queryKey: KEY, queryFn: ({ signal }) => request('/integrations/whatsapp', { schema: statusSchema, signal }) });
  const refresh = () => qc.invalidateQueries({ queryKey: KEY });
  const onError = (title: string) => (err: unknown) => {
    if (err instanceof SignupCancelled) return;
    toast.error(title, err instanceof ApiError || err instanceof Error ? err.message : undefined);
  };

  const embedded = status.data?.embedded ?? null;
  const oneClick = useMutation({
    mutationFn: async () => {
      // בפיתוח, בלי אפליקציית Meta: חיבור דמו שלא יוצא לרשת.
      const signup = embedded ? await launchWhatsAppSignup(embedded) : { code: 'demo', phoneNumberId: '100000000001', wabaId: '200000000002' };
      return request('/integrations/whatsapp/embedded', { method: 'POST', body: signup });
    },
    onSuccess: async () => {
      toast.success(t('whatsapp.connected'), t('whatsapp.connectedHint'));
      await refresh();
    },
    onError: onError(t('whatsapp.notCompleted')),
  });
  const disconnect = useMutation({
    mutationFn: () => request('/integrations/whatsapp', { method: 'DELETE' }),
    onSuccess: async () => {
      toast.success(t('whatsapp.disconnected'), t('whatsapp.disconnectedHint'));
      await refresh();
    },
    onError: onError(t('whatsapp.disconnectFailed')),
  });

  const data = status.data;
  const s = data?.settings;
  const connected = data?.connected ?? false;
  const canOneClick = !!embedded || !!data?.demo;

  return (
    <Card>
      <CardHeader className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="grid size-9 shrink-0 place-items-center rounded-(--radius-md) bg-success-subtle text-success">
            <MessageCircle className="size-4" aria-hidden />
          </span>
          <div>
            <CardTitle className="text-base">WhatsApp</CardTitle>
            <CardDescription>{t('whatsapp.description')}</CardDescription>
          </div>
        </div>
        {connected ? (
          <Badge tone="success" className="shrink-0 whitespace-nowrap">
            {t('whatsapp.badgeConnected')}
          </Badge>
        ) : data?.status === 'EXPIRED' ? (
          <Badge tone="warning" className="shrink-0 whitespace-nowrap">
            {t('whatsapp.badgeReconnect')}
          </Badge>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4">
        {data?.lastError && !connected ? <p className="text-xs text-danger">{data.lastError}</p> : null}

        {connected && s ? (
          <>
            <p className="text-sm text-fg">
              {t('whatsapp.sendingFrom')}
              <bdi dir="ltr">{s.displayPhoneNumber || s.phoneNumberId}</bdi>
              {s.verifiedName ? <span className="text-fg-muted"> · {s.verifiedName}</span> : null}
            </p>
            <TemplateState settings={s} />
            <details className="group rounded-(--radius-md) border border-border px-3 py-2">
              <summary className="cursor-pointer text-xs font-medium text-fg-muted">{t('whatsapp.advanced')}</summary>
              <div className="pt-3">
                <TemplateForm settings={s} onSaved={() => void refresh()} />
              </div>
            </details>
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" loading={disconnect.isPending} onClick={() => disconnect.mutate()}>
                <Unplug aria-hidden />
                {t('whatsapp.disconnect')}
              </Button>
            </div>
          </>
        ) : (
          <>
            {canOneClick ? (
              <div className="space-y-2 rounded-(--radius-lg) bg-surface-sunken p-4">
                <Button loading={oneClick.isPending} onClick={() => oneClick.mutate()} data-track="integration.whatsapp.connect">
                  <MessageCircle aria-hidden />
                  {embedded ? t('whatsapp.connectButton') : t('whatsapp.demoButton')}
                </Button>
                <p className="text-xs text-fg-muted">
                  {embedded ? t('whatsapp.embeddedHint') : t('whatsapp.demoHint')}
                </p>
              </div>
            ) : (
              <p className="rounded-(--radius-md) bg-surface-sunken px-3 py-2 text-xs text-fg-muted">
                {t('whatsapp.oneClickUnavailable')}
              </p>
            )}
            <details className="rounded-(--radius-md) border border-border px-3 py-2" open={!canOneClick}>
              <summary className="cursor-pointer text-xs font-medium text-fg-muted">{t('whatsapp.manual')}</summary>
              <div className="pt-3">
                <ManualConnect onConnected={() => void refresh()} />
              </div>
            </details>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** מצב תבנית ההודעות — מה שקובע אם אפשר לשלוח גם ללקוח שלא כתב לאחרונה. */
function TemplateState({ settings: s }: { settings: z.infer<typeof settingsSchema> }) {
  const { t } = useTranslation('settings');
  if (s.templateParams === 2) {
    const st = s.templateStatus;
    if (st === 'APPROVED') {
      return (
        <p className="flex items-center gap-1.5 text-xs text-success">
          <CheckCircle2 className="size-3.5" aria-hidden />
          {t('whatsapp.templateApproved')}
        </p>
      );
    }
    if (st === 'REJECTED' || st === 'DISABLED' || st === 'PAUSED') {
      return (
        <p className="flex items-center gap-1.5 text-xs text-danger">
          <XCircle className="size-3.5" aria-hidden />
          {t('whatsapp.templateRejected')}
        </p>
      );
    }
    return (
      <p className="flex items-center gap-1.5 text-xs text-fg-muted">
        <Clock className="size-3.5" aria-hidden />
        {t('whatsapp.templatePending')}
      </p>
    );
  }
  return s.templateName ? (
    <p className="text-xs text-fg-muted">
      {t('whatsapp.sendingWithTemplate')}
      <bdi dir="ltr">{s.templateName}</bdi>.
    </p>
  ) : (
    <p className="rounded-(--radius-md) bg-warning-subtle px-3 py-2 text-xs text-warning">
      {t('whatsapp.noTemplate')}
    </p>
  );
}

function TemplateForm({ settings: s, onSaved }: { settings: z.infer<typeof settingsSchema>; onSaved: () => void }) {
  const { t } = useTranslation(['settings', 'common']);
  const toast = useToast();
  const [templateName, setTemplate] = useState(s.templateName ?? '');
  const [templateLanguage, setLanguage] = useState(s.templateLanguage || 'he');
  useEffect(() => {
    setTemplate(s.templateName ?? '');
    setLanguage(s.templateLanguage || 'he');
  }, [s.templateName, s.templateLanguage]);
  const valid = TEMPLATE.test(templateName.trim());
  const save = useMutation({
    mutationFn: () =>
      request('/integrations/whatsapp', {
        method: 'PATCH',
        body: { templateName: templateName.trim() || null, templateLanguage: templateLanguage.trim() || 'he' },
      }),
    onSuccess: () => {
      toast.success(t('whatsapp.templateSaved'));
      onSaved();
    },
    onError: (e) => toast.error(t('common:errors.saveFailed'), e instanceof ApiError ? e.message : undefined),
  });
  return (
    <div className="space-y-3">
      <TemplateFields name={templateName} language={templateLanguage} onName={setTemplate} onLanguage={setLanguage} valid={valid} />
      <Button size="sm" variant="secondary" disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
        {t('whatsapp.saveTemplate')}
      </Button>
    </div>
  );
}

function TemplateFields(p: { name: string; language: string; onName: (v: string) => void; onLanguage: (v: string) => void; valid: boolean }) {
  const { t } = useTranslation('settings');
  return (
    <div className="grid gap-3 sm:grid-cols-[1fr_7rem]">
      <Field
        label={t('whatsapp.templateLabel')}
        htmlFor="wa-template"
        error={p.valid ? undefined : t('whatsapp.templateInvalid')}
        hint={t('whatsapp.templateHint')}
      >
        <Input id="wa-template" dir="ltr" spellCheck={false} placeholder="craftmind_update" value={p.name} onChange={(e) => p.onName(e.target.value)} />
      </Field>
      <Field label={t('whatsapp.languageLabel')} htmlFor="wa-lang">
        <Input id="wa-lang" dir="ltr" spellCheck={false} value={p.language} onChange={(e) => p.onLanguage(e.target.value)} />
      </Field>
    </div>
  );
}

function ManualConnect({ onConnected }: { onConnected: () => void }) {
  const { t } = useTranslation('settings');
  const toast = useToast();
  const [phoneNumberId, setPhoneId] = useState('');
  const [accessToken, setToken] = useState('');
  const [templateName, setTemplate] = useState('');
  const [templateLanguage, setLanguage] = useState('he');
  const valid = TEMPLATE.test(templateName.trim());
  const canConnect = /^\d{5,30}$/.test(phoneNumberId.trim()) && accessToken.trim().length >= 4 && valid;
  const connect = useMutation({
    mutationFn: () =>
      request('/integrations/whatsapp', {
        method: 'POST',
        body: {
          phoneNumberId: phoneNumberId.trim(),
          accessToken: accessToken.trim(),
          templateName: templateName.trim() || null,
          templateLanguage: templateLanguage.trim() || 'he',
        },
      }),
    onSuccess: () => {
      setToken('');
      toast.success(t('whatsapp.connected'), t('whatsapp.connectedHint'));
      onConnected();
    },
    onError: (e) => toast.error(t('whatsapp.connectFailed'), e instanceof ApiError ? e.message : undefined),
  });
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (canConnect) connect.mutate();
      }}
    >
      <ol className="list-decimal space-y-1 ps-5 text-xs text-fg-muted">
        <li>{t('whatsapp.manualStep1')}</li>
        <li>{t('whatsapp.manualStep2')}</li>
        <li>{t('whatsapp.manualStep3')}</li>
        <li>
          {t('whatsapp.manualStep4Before')}
          <bdi dir="ltr">{'{{1}}'}</bdi>
          {t('whatsapp.manualStep4After')}
        </li>
      </ol>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Phone Number ID" htmlFor="wa-phone-id">
          <Input id="wa-phone-id" dir="ltr" inputMode="numeric" autoComplete="off" value={phoneNumberId} onChange={(e) => setPhoneId(e.target.value)} />
        </Field>
        <Field label={t('whatsapp.tokenLabel')} htmlFor="wa-token">
          <Input id="wa-token" dir="ltr" type="password" autoComplete="off" spellCheck={false} value={accessToken} onChange={(e) => setToken(e.target.value)} />
        </Field>
      </div>
      <TemplateFields name={templateName} language={templateLanguage} onName={setTemplate} onLanguage={setLanguage} valid={valid} />
      <Button type="submit" size="sm" variant="secondary" disabled={!canConnect} loading={connect.isPending} data-track="integration.whatsapp.connect-manual">
        {t('whatsapp.connect')}
      </Button>
    </form>
  );
}
